import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Globe,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';

interface Country {
  _id: string;
  name: string;
  code?: string;
  region?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const CountriesPage: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [newCountry, setNewCountry] = useState({
    name: '',
    code: '',
    region: '',
    description: '',
    isActive: true,
  });

  const { settings } = useSettings();

  useEffect(() => {
    fetchCountries();
  }, []);

  // Soft refresh for countries
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateMasterDataCache();
      fetchCountries();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/countries?limit=1000'); // Request all countries
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to fetch countries');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCountry = async () => {
    try {
      const response = await apiService.post('/countries', newCountry);
      toast.success('Country added successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowAddModal(false);
      setNewCountry({
        name: '',
        code: '',
        region: '',
        description: '',
        isActive: true,
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCountries();
      }, 100);
    } catch (error) {
      toast.error('Failed to add country');
    }
  };

  const handleEditCountry = async () => {
    if (!editingCountry) return;
    
    try {
      const response = await apiService.put(`/countries/${editingCountry._id}`, editingCountry);
      toast.success('Country updated successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowEditModal(false);
      setEditingCountry(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCountries();
      }, 100);
    } catch (error) {
      toast.error('Failed to update country');
    }
  };

  const handleDeleteCountry = async (countryId: string) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      try {
        const response = await apiService.delete(`/countries/${countryId}`);
        toast.success('Country deleted successfully');
        
        // Immediately clear master data cache and force refresh
        apiService.invalidateMasterDataCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchCountries();
        }, 100);
      } catch (error) {
        console.error('Error deleting country:', error);
        toast.error('Failed to delete country');
      }
    }
  };

  const openEditModal = (country: Country) => {
    setEditingCountry(country);
    setShowEditModal(true);
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         country.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         country.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         country.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || country.isActive === (statusFilter === 'active');
    
    return matchesSearch && matchesStatus;
  });

  const exportCountries = async () => {
    try {
      await apiService.download('/products/countries/export', 'countries.xlsx');
      toast.success('Countries exported successfully');
    } catch (error) {
      console.error('Error exporting countries:', error);
      toast.error('Failed to export countries');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>Countries</h1>
          <p style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>
            Manage your countries
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateMasterDataCache(); 
              fetchCountries(); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Country
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportCountries}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      {/* Countries Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Country</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Region</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredCountries.map((country) => (
                <tr key={country._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Globe className="w-5 h-5 text-[var(--color-primary)] mr-2" />
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {country.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {country.code || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {country.region || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {country.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-['Times_New_Roman']">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${country.isActive ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'}`}>{country.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(country.createdAt, settings)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(country)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-['Times_New_Roman']"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCountry(country._id)}
                        className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)] font-['Times_New_Roman']"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>
        {/* Empty State */}
        {filteredCountries.length === 0 && (
          <div className="text-center py-12">
            <Globe className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No countries found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first country.'}
            </p>
          </div>
        )}
      </div>
      {/* Add Country Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Country"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newCountry.name}
            onChange={e => setNewCountry({ ...newCountry, name: e.target.value })}
            placeholder="Enter country name"
            required
          />
          <Input
            label="Code"
            value={newCountry.code}
            onChange={e => setNewCountry({ ...newCountry, code: e.target.value })}
            placeholder="Enter country code"
          />
          <Input
            label="Region"
            value={newCountry.region}
            onChange={e => setNewCountry({ ...newCountry, region: e.target.value })}
            placeholder="Enter region"
          />
          <Input
            label="Description"
            value={newCountry.description}
            onChange={e => setNewCountry({ ...newCountry, description: e.target.value })}
            placeholder="Enter description"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newCountry.isActive ? 'active' : 'inactive'}
              onChange={e => setNewCountry({ ...newCountry, isActive: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowAddModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleAddCountry}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Add Country
          </button>
        </div>
      </Modal>
      {/* Edit Country Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Country"
      >
        {editingCountry && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingCountry.name}
              onChange={e => setEditingCountry({ ...editingCountry, name: e.target.value })}
              placeholder="Enter country name"
              required
            />
            <Input
              label="Code"
              value={editingCountry.code || ''}
              onChange={e => setEditingCountry({ ...editingCountry, code: e.target.value })}
              placeholder="Enter country code"
            />
            <Input
              label="Region"
              value={editingCountry.region || ''}
              onChange={e => setEditingCountry({ ...editingCountry, region: e.target.value })}
              placeholder="Enter region"
            />
            <Input
              label="Description"
              value={editingCountry.description || ''}
              onChange={e => setEditingCountry({ ...editingCountry, description: e.target.value })}
              placeholder="Enter description"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingCountry.isActive ? 'active' : 'inactive'}
                onChange={e => setEditingCountry({ ...editingCountry, isActive: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowEditModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleEditCountry}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Update Country
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CountriesPage; 